import React, { useEffect, useId, useMemo, useState } from "react";
import {
  CATALOG_MATERIAL_TYPE,
  addWorkTypeMaterial,
  deleteWorkTypeMaterial,
  filterCatalogByMaterialType,
  updateWorkTypeMaterial,
} from "../../../../helpers/estimates";
import {
  getMaterialClientUnitPrice,
  getMaterialListPrice,
  getMaterialOurCost,
  hasPriceOverride,
} from "../../../../helpers/estimateLinePricing";
import {
  computeMaterialLineTotal,
  formatCurrency,
} from "../../../../helpers/estimateTotals";
import { filterMaterialsCatalog } from "../../../../helpers/materialSearch";
import { EditableGroup, RowStatus } from "./EditableGroup";
import { RowDelete } from "./RowDelete";
import { CatalogDialog, YourPriceField } from "./editorWidgets";

const BLANK = Object.freeze({
  material: null, custom: false, query: "", unit: "", price: "", quantity: "",
  overrideMode: "", overrideValue: "",
});

function overrideFields(draft) {
  return {
    priceOverrideMode: draft.overrideValue === "" || draft.overrideValue == null ? null : (draft.overrideMode || "amount"),
    priceOverrideValue: draft.overrideValue,
  };
}

function pricedRow(row, draft) {
  return {
    ...row,
    quantity: draft.quantity,
    price_override_mode: overrideFields(draft).priceOverrideMode,
    price_override_value: draft.overrideValue === "" ? null : draft.overrideValue,
  };
}

/** Catalog search combobox; "Use “x” as custom product" covers off-catalog items. */
function ProductPicker({ draft, update, commit, products, disabled, autoFocus }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const results = useMemo(() => filterMaterialsCatalog(products, draft.query, { limit: 8 }), [products, draft.query]);
  const typed = draft.query.trim();
  const options = [
    ...results.map((material) => ({ kind: "catalog", material })),
    ...(typed ? [{ kind: "custom", name: typed }] : []),
  ];

  function choose(option) {
    if (option.kind === "catalog") {
      update({
        material: option.material,
        custom: false,
        query: option.material.material_name,
        unit: option.material.unit_of_measure || "",
        price: "",
        quantity: draft.quantity === "" ? "1" : draft.quantity,
      });
      // Picking a catalog product saves the line straight away (qty defaults to 1, editable after).
      commit?.();
    } else {
      update({ material: null, custom: true, query: option.name });
    }
    setOpen(false);
  }

  if (draft.material) {
    return (
      <div className="eg-picked">
        <span className="eg-picked__name">{draft.material.material_name}</span>
        <span className="eg-picked__meta">
          {[draft.material.sku && `SKU ${draft.material.sku}`, "Catalog"].filter(Boolean).join(" · ")}
        </span>
        <button type="button" className="eg-icon-btn" aria-label="Change product" onClick={() => update({ ...BLANK })}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="eg-combo">
      <input
        className="eg-input"
        role="combobox"
        aria-expanded={open && options.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Product"
        data-col="product"
        placeholder="Search catalog or type a custom product…"
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        value={draft.query}
        onChange={(event) => {
          update({ query: event.target.value, custom: false, material: null });
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(event) => {
          if (!open || options.length === 0) return;
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((i) => Math.min(i + 1, options.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (event.key === "Enter" && !draft.custom) {
            event.preventDefault();
            choose(options[Math.min(active, options.length - 1)]);
          } else if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
          }
        }}
      />
      {open && options.length > 0 ? (
        <ul id={listId} className="eg-combo__list" role="listbox" aria-label="Product results">
          {options.map((option, index) => (
            <li
              key={option.kind === "catalog" ? option.material.material_id : "custom"}
              role="option"
              aria-selected={index === active}
              className={index === active ? "is-active" : ""}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
            >
              {option.kind === "catalog" ? (
                <>
                  <span>{option.material.material_name}</span>
                  <span className="eg-combo__meta">
                    {formatCurrency(option.material.sell_price != null && option.material.sell_price !== "" ? option.material.sell_price : option.material.unit_cost)}
                    {option.material.unit_of_measure ? ` / ${option.material.unit_of_measure}` : ""}
                  </span>
                </>
              ) : (
                <span>Use “{option.name}” as a custom product</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function isNewMaterialComplete(draft) {
  if (draft.material) return true;
  return Boolean(draft.custom && draft.query.trim() && Number(draft.quantity) > 0);
}

function MaterialRow({ row, products, materialsCatalog, isNew, disabled, onSave, onDelete, onDraft, onStatus, focusFirst }) {
  const baseline = isNew ? BLANK : {
    quantity: row.quantity == null ? "" : String(row.quantity),
    overrideMode: row.price_override_mode || "",
    overrideValue: row.price_override_value == null ? "" : String(row.price_override_value),
  };
  return (
    <EditableGroup
      value={baseline}
      isNew={isNew}
      disabled={disabled}
      onSave={onSave}
      onStatus={onStatus}
      onDraft={onDraft}
      isComplete={isNew ? isNewMaterialComplete : undefined}
      testId={`material-row-${isNew ? "new" : row.room_material_id}`}
    >
      {({ draft, update, commit, status }) => {
        const isCustomRow = !isNew && row.material_id == null;
        const preview = isNew
          ? {
            material_id: draft.material?.material_id ?? null,
            quantity: draft.quantity,
            unit_cost_snapshot: draft.material ? draft.material.unit_cost : draft.price,
            sell_price_snapshot: draft.material
              ? (draft.material.sell_price == null || draft.material.sell_price === "" ? null : draft.material.sell_price)
              : (draft.price === "" ? null : draft.price),
            price_override_mode: overrideFields(draft).priceOverrideMode,
            price_override_value: draft.overrideValue === "" ? null : draft.overrideValue,
          }
          : pricedRow(row, draft);
        const listPrice = isNew && !draft.material && !draft.custom ? null : getMaterialListPrice(preview, materialsCatalog);
        const lineTotal = computeMaterialLineTotal(preview, materialsCatalog);
        const ourCost = getMaterialOurCost(preview, materialsCatalog);
        const clientUnit = getMaterialClientUnitPrice(preview, materialsCatalog);
        const showMoney = isNew ? Boolean(draft.material || draft.custom) : true;
        return (
          <tr className="eg-row">
            <td>
              {isNew ? (
                <ProductPicker draft={draft} update={update} commit={commit} products={products} disabled={disabled} autoFocus={focusFirst} />
              ) : (
                <div className="eg-picked">
                  <span className="eg-picked__name">{row.material_name_snapshot}</span>
                  <span className="eg-picked__meta">
                    {[row.sku_snapshot && `SKU ${row.sku_snapshot}`, isCustomRow ? "Custom" : "Catalog"]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              )}
            </td>
            <td>
              <input
                className="eg-input eg-input--num"
                type="number"
                min="0"
                step="any"
                aria-label="Quantity"
                data-col="qty"
                value={draft.quantity}
                disabled={disabled || (isNew && !draft.material && !draft.custom)}
                onChange={(event) => update({ quantity: event.target.value })}
              />
            </td>
            <td>
              {isNew && draft.custom ? (
                <input className="eg-input" aria-label="Unit" data-col="unit" placeholder="ea, bag…" value={draft.unit}
                  onChange={(event) => update({ unit: event.target.value })} />
              ) : (
                <span className="eg-computed">{(isNew ? draft.unit : row.unit_of_measure_snapshot) || "—"}</span>
              )}
            </td>
            <td className="eg-num">
              <div className="eg-price">
                {isNew && draft.custom ? (
                  <input className="eg-input eg-input--num" type="number" min="0" step="any" aria-label="Unit price"
                    data-col="price" placeholder="0.00" value={draft.price}
                    onChange={(event) => update({ price: event.target.value })} />
                ) : (
                  <span className="eg-computed" title="Catalog client price">
                    {listPrice == null ? "—" : formatCurrency(listPrice)}
                    {hasPriceOverride(preview) ? <span className="eg-mark">Adjusted</span> : null}
                  </span>
                )}
                {showMoney ? (
                  <span className="eg-internal">
                    <span>Cost {formatCurrency(ourCost)}</span>
                    <span>Margin {formatCurrency(clientUnit - ourCost)}</span>
                  </span>
                ) : null}
              </div>
            </td>
            <td>
              <YourPriceField
                mode={draft.overrideMode}
                value={draft.overrideValue}
                unitPrice={listPrice || 0}
                disabled={disabled || (isNew && !draft.material && !draft.custom)}
                onChange={(next) => update({ overrideMode: next.mode, overrideValue: next.value })}
              />
            </td>
            <td className="eg-num eg-computed" aria-label="Line total">{formatCurrency(lineTotal)}</td>
            <td className="eg-actions"><RowStatus status={status} /></td>
            <td className="eg-delete">
              {!isNew ? (
                <RowDelete label={row.material_name_snapshot || "material"} disabled={disabled} onConfirm={() => onDelete(row)} />
              ) : null}
            </td>
          </tr>
        );
      }}
    </EditableGroup>
  );
}

/**
 * Materials grid. Existing helpers only: addWorkTypeMaterial (catalog or
 * custom product), updateWorkTypeMaterial (quantity), deleteWorkTypeMaterial.
 *
 * Unit price is read-only on saved lines: those helpers cannot change it and
 * estimateTotals prices catalog lines from the live catalog, so an
 * estimate-only price override would need new data/behaviour (see PR notes).
 */
export function MaterialsTable({ workType, materialsCatalog, actor, editable, onChanged, onDraft, onStatus }) {
  const [focusToken, setFocusToken] = useState(0);
  const [catalogOpen, setCatalogOpen] = useState(false);
  useEffect(() => {
    if (!focusToken) return undefined;
    document.querySelector('[data-testid="material-row-new"] input')?.focus();
    return undefined;
  }, [focusToken]);
  const products = useMemo(
    () => filterCatalogByMaterialType(materialsCatalog, CATALOG_MATERIAL_TYPE.PRODUCT),
    [materialsCatalog],
  );
  const rows = workType.materials || [];
  const featureId = workType.estimate_feature_id;

  return (
    <section className="ed-section" aria-labelledby="ed-materials-h">
      <div className="ed-section__bar">
        <h3 id="ed-materials-h" className="ed-section__title">Materials</h3>
        <button type="button" className="eg-link" onClick={() => setCatalogOpen(true)}>View catalog</button>
      </div>
      <div className="ed-tablewrap">
        <table className="eg-table">
          <thead>
            <tr>
              <th>Product</th>
              <th className="eg-num" style={{ width: 68 }}>Qty</th>
              <th style={{ width: 56 }}>Unit</th>
              <th className="eg-num" style={{ width: 128 }}>Unit price</th>
              <th style={{ width: 128 }}>Your price</th>
              <th className="eg-num" style={{ width: 84 }}>Line total</th>
              <th style={{ width: 72 }} aria-label="Status" />
              <th style={{ width: 36 }} aria-label="Remove" />
            </tr>
          </thead>
          {rows.map((row) => (
            <MaterialRow
              key={row.room_material_id}
              row={row}
              products={products}
              materialsCatalog={materialsCatalog}
              disabled={!editable}
              onDraft={(draft) => onDraft(row.room_material_id, draft ? {
                quantity: draft.quantity === "" ? null : draft.quantity,
                price_override_mode: overrideFields(draft).priceOverrideMode,
                price_override_value: draft.overrideValue === "" ? null : draft.overrideValue,
              } : null)}
              onSave={async (draft) => {
                await updateWorkTypeMaterial(actor, row.room_material_id, {
                  quantity: draft.quantity,
                  notes: row.notes,
                  ...overrideFields(draft),
                });
                await onChanged();
              }}
              onDelete={async () => {
                onStatus("saving");
                try {
                  await deleteWorkTypeMaterial(actor, row.room_material_id);
                  await onChanged();
                  onStatus("saved");
                } catch (err) {
                  onStatus("error", err.message || "Unable to remove material.");
                }
              }}
              onStatus={onStatus}
            />
          ))}
          {editable ? (
            <MaterialRow
              key={`new-${focusToken}`}
              isNew
              products={products}
              materialsCatalog={materialsCatalog}
              focusFirst={focusToken > 0}
              onStatus={onStatus}
              onSave={async (draft) => {
                if (!draft.material && !draft.custom) throw new Error("Pick a catalog product or use a custom one.");
                const pricing = { quantity: draft.quantity, notes: "", ...overrideFields(draft) };
                if (draft.material) {
                  await addWorkTypeMaterial(actor, featureId, { material: draft.material, ...pricing });
                } else {
                  await addWorkTypeMaterial(actor, featureId, {
                    customProduct: { productName: draft.query, unitCost: draft.price, sellPrice: draft.price, unitType: draft.unit },
                    ...pricing,
                  });
                }
                await onChanged();
                setFocusToken((t) => t + 1);
              }}
            />
          ) : null}
        </table>
      </div>
      {editable ? (
        <p className="ed-hint">Search the catalog (↑ ↓ Enter) or type a custom product. Your price is optional: $ is a fixed unit price, % is plus or minus that percent. It saves when you leave the row (or press Enter).</p>
      ) : null}
      {catalogOpen ? (
        <CatalogDialog title="Product catalog" rows={products} showCost onClose={() => setCatalogOpen(false)} />
      ) : null}
    </section>
  );
}
